(() => {
  const ELEMENT_NAME = "the-padel-shop-collection-racket-filter";
  const HIDDEN_CLASS = "tps-racket-fit-hidden";

  function finderTagValues(tags, key) {
    const prefix = `finder:${key}:`;
    return (tags || [])
      .map(tag => String(tag).trim().toLowerCase())
      .filter(tag => tag.startsWith(prefix))
      .map(tag => tag.slice(prefix.length).trim())
      .filter(Boolean);
  }

  function profileFromTags(product) {
    const profile = {
      handle: product.handle,
      level: finderTagValues(product.tags, "level")[0],
      style: finderTagValues(product.tags, "style")[0],
      play: finderTagValues(product.tags, "play")[0],
      weight: finderTagValues(product.tags, "weight")[0],
      balance: finderTagValues(product.tags, "balance")[0],
      shape: finderTagValues(product.tags, "shape")[0],
      feel: finderTagValues(product.tags, "feel")[0],
      comfortTech: finderTagValues(product.tags, "comfort-tech")[0] === "true"
    };

    const required = ["level", "style", "play", "weight", "balance", "shape", "feel"];
    return required.every(key => Boolean(profile[key])) ? profile : null;
  }

  function matchesExperience(profile, answer) {
    if (!answer) return true;
    if (answer === "new") return profile.level === "developing";
    if (answer === "developing") return ["developing", "intermediate"].includes(profile.level);
    if (answer === "experienced") return ["intermediate", "advanced"].includes(profile.level);
    return true;
  }

  function matchesGoal(profile, answer, experience) {
    if (!answer) return true;
    if (answer === "control") return profile.play === "control";
    if (answer === "balanced") {
      if (experience === "new") return ["control", "control-power"].includes(profile.play);
      return profile.play === "control-power" || profile.style === "all-rounder";
    }
    if (answer === "power") {
      if (experience === "new") return profile.play === "control-power";
      return profile.play === "power"
        || (profile.play === "control-power" && profile.style === "attacking");
    }
    return true;
  }

  function matchesFeel(profile, answer) {
    if (!answer) return true;
    if (answer === "light") return ["super-light", "light"].includes(profile.weight);
    if (answer === "versatile") {
      return ["light", "medium"].includes(profile.weight)
        && ["head-light", "even"].includes(profile.balance);
    }
    if (answer === "solid") return ["medium", "heavy"].includes(profile.weight);
    if (answer === "comfort") return profile.feel === "soft" || profile.comfortTech === true;
    return true;
  }

  function matchesProfile(profile, answers) {
    if (!profile) return false;
    return matchesExperience(profile, answers.experience)
      && matchesGoal(profile, answers.goal, answers.experience)
      && matchesFeel(profile, answers.feel);
  }

  function handleFromCard(card) {
    if (card.dataset.tpsRacketHandle) return card.dataset.tpsRacketHandle;

    const links = card.querySelectorAll('a[href*="/products/"]');
    for (const link of links) {
      try {
        const url = new URL(link.getAttribute("href"), window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/);
        if (!match) continue;
        const handle = decodeURIComponent(match[1]);
        card.dataset.tpsRacketHandle = handle;
        return handle;
      } catch {
        // Continue to the next product link.
      }
    }

    return "";
  }

  class ThePadelShopCollectionRacketFilter extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === "true") return;
      this.dataset.initialised = "true";

      this.selects = Array.from(this.querySelectorAll("[data-tps-filter]"));
      this.status = this.querySelector("[data-tps-status]");
      this.matchCount = this.querySelector("[data-tps-match-count]");
      this.statusCopy = this.querySelector("[data-tps-status-copy]");
      this.clearButton = this.querySelector("[data-tps-clear]");
      this.emptyMessage = this.querySelector("[data-tps-empty]");
      this.updateThemeCount = this.dataset.updateThemeCount !== "false";
      this.totalProducts = Number(this.dataset.totalProducts || 0);
      this.catalogByHandle = new Map();
      this.collectionProfiles = [];
      this.observer = null;
      this.updateTimer = null;

      this.loadProfiles();
      this.bindEvents();
      this.observeCollectionChanges();
      this.applyFilters();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.updateTimer) window.clearTimeout(this.updateTimer);
    }

    loadProfiles() {
      const catalog = window.TPS_RACKET_FINDER_DATA?.rackets || [];
      catalog.forEach(profile => {
        if (profile.handle) this.catalogByHandle.set(profile.handle, profile);
      });

      let products = [];
      try {
        products = JSON.parse(this.querySelector("[data-tps-collection-products]")?.textContent || "[]");
      } catch {
        products = [];
      }

      this.collectionProfiles = products.map(product => {
        const profile = this.catalogByHandle.get(product.handle) || profileFromTags(product);
        return profile ? { ...profile, handle: product.handle } : null;
      }).filter(Boolean);

      const profiledHandles = new Set(this.collectionProfiles.map(profile => profile.handle));
      const missing = products
        .map(product => product.handle)
        .filter(handle => handle && !profiledHandles.has(handle));

      if (missing.length) {
        console.info("ThePadelShop collection guide: add racket finder data for", missing);
      }
    }

    bindEvents() {
      this.selects.forEach(select => {
        select.addEventListener("change", () => this.applyFilters());
      });

      this.clearButton?.addEventListener("click", () => {
        this.selects.forEach(select => {
          select.value = "";
        });
        this.applyFilters();
      });

      document.addEventListener("shopify:section:load", () => this.scheduleApply());
    }

    getAnswers() {
      return this.selects.reduce((answers, select) => {
        answers[select.dataset.tpsFilter] = select.value;
        return answers;
      }, {});
    }

    getActiveCount(answers) {
      return Object.values(answers).filter(Boolean).length;
    }

    matchingProfiles(answers) {
      return this.collectionProfiles.filter(profile => matchesProfile(profile, answers));
    }

    productCards() {
      const grid = document.querySelector('[data-testid="product-grid"], .product-grid');
      if (!grid) return [];
      return Array.from(grid.querySelectorAll(".product-grid__item"));
    }

    filterCards(answers, activeCount) {
      const visibleHandles = new Set(this.matchingProfiles(answers).map(profile => profile.handle));

      this.productCards().forEach(card => {
        const handle = handleFromCard(card);
        const shouldHide = activeCount > 0 && (!handle || !visibleHandles.has(handle));
        card.classList.toggle(HIDDEN_CLASS, shouldHide);
        card.setAttribute("aria-hidden", shouldHide ? "true" : "false");
      });
    }

    updateNativeCounts(total, activeCount) {
      if (!this.updateThemeCount) return;

      document.querySelectorAll('[data-testid="products-count"] [role="status"]').forEach(node => {
        if (!node.dataset.tpsOriginalCount) node.dataset.tpsOriginalCount = node.textContent.trim();
        const nextText = activeCount > 0
          ? `${total} ${total === 1 ? "ITEM" : "ITEMS"}`
          : node.dataset.tpsOriginalCount;
        if (node.textContent.trim() !== nextText) node.textContent = nextText;
      });
    }

    updateStatus(total, activeCount) {
      this.selects.forEach(select => {
        select.closest("[data-tps-field]")?.classList.toggle("is-selected", Boolean(select.value));
      });

      if (activeCount === 0) {
        if (this.status) this.status.hidden = true;
        if (this.emptyMessage) this.emptyMessage.hidden = true;
        return;
      }

      if (this.status) this.status.hidden = false;
      if (this.matchCount) {
        this.matchCount.textContent = `${total} ${total === 1 ? "racket" : "rackets"}`;
      }
      if (this.statusCopy) {
        this.statusCopy.textContent = activeCount === 1
          ? "match your first choice. Add another answer to narrow them further."
          : activeCount === 2
            ? "match your choices. One more answer will refine the results."
            : "match all three of your choices.";
      }
      if (this.emptyMessage) this.emptyMessage.hidden = total !== 0;
    }

    applyFilters() {
      const answers = this.getAnswers();
      const activeCount = this.getActiveCount(answers);
      const total = activeCount > 0 ? this.matchingProfiles(answers).length : this.totalProducts;

      this.filterCards(answers, activeCount);
      this.updateStatus(total, activeCount);
      this.updateNativeCounts(total, activeCount);
    }

    scheduleApply() {
      if (this.updateTimer) window.clearTimeout(this.updateTimer);
      this.updateTimer = window.setTimeout(() => this.applyFilters(), 80);
    }

    observeCollectionChanges() {
      this.observer = new MutationObserver(mutations => {
        const gridChanged = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => {
            return node.nodeType === Node.ELEMENT_NODE
              && (node.matches?.(".product-grid__item, .product-grid")
                || node.querySelector?.(".product-grid__item, .product-grid"));
          });
        });

        if (gridChanged) this.scheduleApply();
      });

      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, ThePadelShopCollectionRacketFilter);
  }
})();